import { applySnapshot, cast, flow, types } from 'mobx-state-tree';
import { Telephony } from '../../http-requests';
import { formatDateToISO, killNulls } from '../../utils';
import { formatBillsecToSeconds } from '../../views/authorized/pages/telephony/utils';
import { RequestStateModel } from './general-models';

//TODO: исправить импорт или передлать принцип типизации
import {
  ICall,
  ITranscriptionResult,
} from '../../views/authorized/pages/telephony/types';

const IdAndNameModel = types.model({
  id: types.optional(types.string, ''),
  name: types.optional(types.string, ''),
});

const ProviderModel = types.model({
  id: types.optional(types.string, ''),
  str_name: types.optional(types.string, ''),
});

const CommentsModel = types.model({
  author: types.optional(IdAndNameModel, {}),
  date: types.optional(types.string, ''),
  text: types.optional(types.string, ''),
});

const WorkerModel = types.model({
  id: types.optional(types.string, ''),
  name: types.optional(types.string, ''),
  phone_number: types.optional(types.string, ''),
  cell_phone: types.optional(types.maybeNull(types.string), ''),
  email: types.optional(types.maybeNull(types.string), ''),
  address: types.optional(types.maybeNull(types.string), ''),
  provider: types.optional(types.maybeNull(ProviderModel), {}),
  department: types.optional(types.maybeNull(IdAndNameModel), {}),
  position: types.optional(types.maybeNull(IdAndNameModel), {}),
  _type: types.optional(types.array(types.string), []),
});

const CallerModel = types.model({
  id: types.optional(types.maybeNull(types.string), ''),
  name: types.optional(types.maybeNull(types.string), ''),
  phone_number: types.optional(types.maybeNull(types.string), ''),
  email: types.optional(types.maybeNull(types.string), ''),
  address: types.optional(types.maybeNull(types.string), ''),
  provider: types.optional(types.maybeNull(ProviderModel), {}),
  department: types.optional(types.maybeNull(IdAndNameModel), {}),
  position: types.optional(types.maybeNull(IdAndNameModel), {}),
  _type: types.optional(types.array(types.string), []),
});

const RatingModel = types.model({
  date: types.optional(types.string, ''),
  rating: types.optional(types.string, ''),
  author: types.optional(IdAndNameModel, {}),
});

const AttemptsModel = types.model({
  full_name: types.optional(types.maybeNull(types.string), ''),
  id: types.optional(types.maybeNull(types.string), ''),
  phone_number: types.optional(types.maybeNull(types.string), ''),
  position: types.optional(types.maybeNull(types.string), ''),
  time: types.optional(types.string, ''),
});

const CallerNowModel = types.model({
  id: types.optional(types.maybeNull(types.string), ''),
  full_name: types.optional(types.maybeNull(types.string), ''),
  time: types.optional(types.maybeNull(types.string), ''),
  position: types.optional(types.maybeNull(types.string), ''),
  phone_number: types.optional(types.maybeNull(types.string), ''),
});

const RedialModel = types.model({
  active: types.optional(types.boolean, false),
  caller_now: types.optional(types.maybeNull(CallerNowModel), {}),
  counter: types.optional(types.number, 0),
  attempts: types.optional(types.maybeNull(types.array(AttemptsModel)), []),
});

const RedirectionMapModel = types.model({
  id: types.optional(types.string, ''),
  name: types.optional(types.string, ''),
  billsec: types.optional(types.number, 0),
  status_forward: types.optional(types.string, ''),
  phone_number: types.optional(types.string, ''),
  cell_phone: types.optional(types.maybeNull(types.string), ''),
  address: types.optional(types.maybeNull(types.string), ''),
  provider: types.optional(types.maybeNull(ProviderModel), {}),
  department: types.optional(types.maybeNull(IdAndNameModel), {}),
  position: types.optional(types.maybeNull(IdAndNameModel), {}),
  email: types.optional(types.maybeNull(types.string), ''),
  _type: types.optional(types.array(types.string), []),
});

const TagModel = types.model({
  author: types.optional(IdAndNameModel, {}),
  date: types.optional(types.string, ''),
  tag: types.optional(types.string, ''),
});

// const TasksModel = types.model({
//   url_address: types.optional(types.string, ''),
//   number: types.optional(types.string, ''),
// });

const CallModel = types
  .model({
    id: types.optional(types.identifier, ''),
    calldate: types.optional(types.string, ''),
    billsec: types.optional(types.number, 0),
    record_url: types.optional(types.maybeNull(types.string), ''),
    status_call: types.optional(types.string, ''),
    worker: types.optional(WorkerModel, {}),
    workers: types.optional(types.array(WorkerModel), []),
    caller: types.optional(CallerModel, {}),
    note: types.optional(types.maybeNull(CommentsModel), {}),
    comments: types.optional(types.array(CommentsModel), []),
    tag: types.optional(types.maybeNull(TagModel), {}),
    rating: types.optional(types.maybeNull(RatingModel), {}),
    redirection_map: types.optional(types.array(RedirectionMapModel), []),
    uniqueid: types.optional(types.string, ''),
    redial: types.optional(types.maybeNull(RedialModel), {}),
    result: types.optional(types.string, ''),
    ml_transcription_result: types.optional(
      types.maybeNull(types.frozen<ITranscriptionResult>()),
      null
    ),
    // TODO пока не реализовано
    // tasks: types.optional(types.maybeNull(types.array(TasksModel)), []),
    // tickets: types.optional(types.array(types.string), []),
    // requests: types.optional(types.array(types.string), []),
    // time_answer: types.optional(types.maybeNull(types.string), ''),
  })
  .views((self) => ({
    get missed() {
      return [
        'REGULAR_INBOUND_NO_ANSWER',
        'REGULAR_OUTBOUND_NO_ANSWER',
        'REGULAR_INWARD_NO_ANSWER',
        'CALLBACK_INBOUND_NO_ANSWER',
        'CALLBACK_INWARD_NO_ANSWER',
      ].includes(self.status_call);
    },
  }));

const ParamsModel = types.model({
  offset: types.optional(types.number, 0),
  limit: types.optional(types.number, 0),
  ordering: types.optional(types.string, ''),
});

const MissedCallBackModel = types
  .model({
    full_name: types.optional(types.string, ''),
    phone_number: types.optional(types.string, ''),
    position: types.optional(types.string, ''),
    missedCall: types.optional(types.boolean, false),
  })
  .actions((self) => ({
    setMissedCallback(value: boolean) {
      self.missedCall = value;
    },
    getMissedCallbackInfo(data: any) {
      self.phone_number = data.another_operator.phone_number;
      self.full_name = data.another_operator.full_name;
      self.position = data.another_operator.position;
    },
  }));

const FiltersModel = types
  .model({
    accountName: types.optional(types.string, ''),
    statusCall: types.optional(types.string, ''),
    datetimeGte: types.optional(types.maybeNull(types.Date), null),
    datetimeLte: types.optional(types.maybeNull(types.Date), null),
    billsecGte: types.optional(types.string, ''),
    billsecLte: types.optional(types.string, ''),
    datetimeError: types.optional(types.boolean, false),
    billsecError: types.optional(types.boolean, false),
    phoneNumberError: types.optional(types.boolean, false),
    accountNumberError: types.optional(types.boolean, false),
    billsecErrorMessage: types.optional(types.string, ''),
    phoneNumber: types.optional(types.string, ''),
    accountNumber: types.optional(types.string, ''),
    tag: types.optional(types.string, ''),
  })
  .actions((self) => ({
    setAccountName(name: string) {
      self.accountName = name;
    },
    setStatusCall(status: string) {
      self.statusCall = status;
    },
    setDatetimeGte(date: Date | null) {
      self.datetimeGte = date;
    },
    setDatetimeLte(date: Date | null) {
      self.datetimeLte = date;
    },
    setBillsecGte(billsec: string) {
      self.billsecGte = billsec;
    },
    setBillsecLte(billsec: string) {
      self.billsecLte = billsec;
    },
    setDatetimeError(error: boolean) {
      self.datetimeError = error;
    },
    setBillsecError(error: boolean) {
      self.billsecError = error;
    },
    setPhoneNumberError(error: boolean) {
      self.phoneNumberError = error;
    },
    setAccountNumberError(error: boolean) {
      self.accountNumberError = error;
    },
    setBillsecErrorMessage(message: string) {
      self.billsecErrorMessage = message;
    },
    setPhoneNumber(phone: string) {
      self.phoneNumber = phone;
    },
    setAccountNumber(account: string) {
      self.accountNumber = account;
    },
    setTag(tag: string) {
      self.tag = tag;
    },
  }));

const initialSortParam = 'calldate';

const initialParams = {
  offset: 0,
  limit: 20,
  ordering: `-${initialSortParam}`,
};

const RequestsModel = types.model({
  volumeNotification: types.optional(RequestStateModel, {}),
  changeCallerRequest: types.optional(RequestStateModel, {}),
  updateCall: types.optional(RequestStateModel, {}),
});

const VolumeNotificationModel = types.model({
  dnd: types.optional(types.boolean, false),
  url_ring: types.optional(types.string, ''),
});

// CallerInfoModel для обратной совместимости с формами вне карточки звонка
// Основная логика теперь в callCardStore.ts
const CallerInfoModel = types
  .model({
    name: types.optional(types.string, ''),
    email: types.optional(types.string, ''),
    phone_number: types.optional(types.string, ''),
    first_number: types.optional(types.string, ''),
    address: types.optional(types.string, ''),
    strNumber: types.optional(types.string, ''),
  })
  .actions((self) => ({
    setCallerActivePhone(str: string) {
      self.first_number = str;
    },

    setCallerPhone(str: string) {
      self.phone_number = str;
    },

    setCallerAddress(str: string) {
      self.address = str;
    },

    setCallerNumber(str: string) {
      self.strNumber = str;
    },

    setCallerName(str: string) {
      self.name = str;
    },

    setCallerEmail(str: string) {
      self.email = str;
    },

    setCallerInfo(
      name: string,
      email: string,
      phone_number: string,
      address: string,
      number: string = ''
    ) {
      self.name = name;
      self.email = email;
      self.phone_number = phone_number;
      self.address = address;
      self.strNumber = number;
    },
  }));

/** TODO: Надо объединять между собой TelephonyStore и CallCardStore в единый CallsStore
 * со своими сабмоделями. Слишком много бесполезных полей и методов, ужасная читаемость, плохая
 * типизация, разрозненность методов и моделей, по факту касающихся одной сущности - звонков. */
export const TelephonyStore = types
  .model({
    statuses: types.optional(RequestsModel, {}),
    calls: types.optional(types.array(CallModel), []),
    selectedCall: types.maybeNull(types.reference(CallModel)),
    selectedDepartment: types.optional(types.string, 'my_calls'),
    params: types.optional(ParamsModel, initialParams),
    callerInfo: types.optional(CallerInfoModel, {}),
    filters: types.optional(FiltersModel, {}),
    textValue: types.optional(types.string, ''),
    focusTextarea: types.optional(types.boolean, false),
    volumeNotification: types.optional(VolumeNotificationModel, {}),
    sortDirection: types.optional(types.boolean, true),
    currentSortParam: types.optional(types.string, initialSortParam),
    showFilters: types.optional(types.boolean, false),
    showDetails: types.optional(types.boolean, false),
    callerPhone: types.optional(types.string, ''),
    callerAddress: types.optional(types.string, ''),
    allCallsSelected: types.optional(types.boolean, true),
    hasNextData: types.optional(types.boolean, true),
    count: types.optional(types.number, 0),
    loading: types.optional(types.boolean, false),
    userCanUpdateCall: types.optional(types.boolean, false),
    userCanReadOwnCalls: types.optional(types.boolean, false),
    userCanReadDepartmentCalls: types.optional(types.boolean, false),
    userCanReadProviderCalls: types.optional(types.boolean, false),
    isNowPlaying: types.optional(types.maybeNull(types.string), null),
    // Общая громкость аудиозаписей (0–1)
    audioVolume: types.optional(types.number, 1),
    missedCallback: types.optional(MissedCallBackModel, {}),
  })
  .views((self) => ({
    get reportType() {
      return self.allCallsSelected ? '' : 'missed_';
    },

    get reportUrl() {
      switch (self.selectedDepartment) {
        case 'my_calls':
          return 'account';
        case 'my_department':
          return 'my/department';
        default:
          return `department/${self.selectedDepartment}`;
      }
    },

    get updateUrl() {
      switch (self.selectedDepartment) {
        case 'my_calls':
          return 'my_call';
        case 'my_department':
          return 'my_department_call';
        default:
          return 'department_call';
      }
    },
  }))
  .actions((self) => ({
    setIsNowPlaying(audio: string | null) {
      self.isNowPlaying = audio;
    },

    setAudioVolume(volume: number) {
      self.audioVolume = volume;
    },

    setAllCallsSelected(bool: boolean) {
      self.allCallsSelected = bool;
    },

    setShowDetails(bool: boolean) {
      self.showDetails = bool;
    },
    // Legacy: для обратной совместимости с формами вне карточки звонка
    // CallCard теперь использует свой собственный callerInfo.clearCallerInfo()
    clearCallerInfo() {
      applySnapshot(self.callerInfo, {});
    },

    setCommentValue(str: string) {
      self.textValue = str;
    },

    setFocusTextArea(bool: boolean) {
      self.focusTextarea = bool;
    },

    increaseOffset() {
      self.params.offset += self.params.limit;
    },

    clearOffset() {
      self.params.offset = 0;
    },

    setSelectedCall(call: any) {
      self.selectedCall = call;
    },

    removeNotRelevantCall(id: string) {
      self.selectedCall = null;
      self.showDetails = false;

      self.calls = cast(self.calls.filter((call: ICall) => call.id !== id));
    },

    setSelectedDepartment(id: string) {
      self.selectedDepartment = id;
    },

    setOrderParam(value: string) {
      // Проверяем, равен ли переданный параметр, по которому надо сортировать тому, что храним. Нужно для того, чтобы сортировка по разным полям всегда начиналась с убывания. Если сортируем несколько раз по одному полю - то чередуем убывание и возрастание (напр. calldate с -calldate)
      if (self.currentSortParam !== value) {
        self.sortDirection = true;
        self.currentSortParam = value;
      } else {
        self.sortDirection = !self.sortDirection;
      }

      self.params.ordering = self.sortDirection ? `-${value}` : value;
    },

    setUserCanUpdateCalls() {
      self.userCanUpdateCall = true;
    },

    setUserCanReadOwnCalls() {
      self.userCanReadOwnCalls = true;
    },

    setUserCanReadDepartmentCalls() {
      self.userCanReadDepartmentCalls = true;
    },

    setUserCanReadProviderCalls() {
      self.userCanReadProviderCalls = true;
    },

    setShowFilters(show: boolean) {
      self.showFilters = show;
    },

    clearFilters() {
      applySnapshot(self.filters, {});
    },

    getCalls: flow(function* (clearSort: boolean) {
      try {
        self.loading = true;

        if (!self.showDetails) {
          self.selectedCall = null;
        }

        if (clearSort) {
          self.currentSortParam = initialSortParam;
          self.sortDirection = true;
          self.params.ordering = `-${initialSortParam}`;
          self.params.offset = 0;
        }

        const convertPhoneNumber = (): string => {
          const phone = self.filters.phoneNumber.replace(/\D/g, '');

          return phone.length === 11 ? phone.substring(1) : phone;
        };

        const filters = killNulls({
          account_name: self.filters.accountName,
          status_call: self.filters.statusCall,
          datetime_gte: self.filters.datetimeGte
            ? formatDateToISO(self.filters.datetimeGte)
            : null,
          datetime_lte: self.filters.datetimeLte
            ? formatDateToISO(self.filters.datetimeLte)
            : null,
          billsec_gte: self.filters.billsecGte
            ? formatBillsecToSeconds(self.filters.billsecGte)
            : null,
          billsec_lte: self.filters.billsecLte
            ? formatBillsecToSeconds(self.filters.billsecLte)
            : null,
          phone_number: self.filters.phoneNumber ? convertPhoneNumber() : null,
          account_number: self.filters.accountNumber,
          tag: self.filters.tag,
        });

        const response = yield Telephony.GetCallsList(
          self.reportUrl,
          self.reportType,
          {
            ...self.params,
            offset: 0,
            limit: self.params.offset + initialParams.limit,
            ...filters,
          }
        );

        self.calls = response.results;

        self.hasNextData = !!response.next;
        self.count = response.count;
      } catch (e) {
        console.error(e);
      } finally {
        self.loading = false;
      }
    }),

    getDnd: flow(function* () {
      try {
        self.statuses.volumeNotification.fulfilled = false;
        self.statuses.volumeNotification.error = false;
        self.statuses.volumeNotification.pending = true;
        self.volumeNotification = yield Telephony.GetDndMode();
        self.statuses.volumeNotification.fulfilled = true;

        return self.volumeNotification;
      } catch (e) {
        self.statuses.volumeNotification.error = true;
        console.error(e);
      } finally {
        self.statuses.volumeNotification.pending = false;
      }
    }),

    changeNotification: flow(function* (data: {
      dnd_status: string;
      extension: string;
    }) {
      try {
        self.statuses.volumeNotification.fulfilled = false;
        self.statuses.volumeNotification.error = false;
        self.statuses.volumeNotification.pending = true;
        self.volumeNotification = yield Telephony.PostDndMode(data);
        self.statuses.volumeNotification.fulfilled = true;

        return self.volumeNotification;
      } catch (e) {
        self.statuses.volumeNotification.error = true;
        console.error(e);
      } finally {
        self.statuses.volumeNotification.pending = false;
      }
    }),

    HungUpCall: flow(function* (callId: string) {
      try {
        const response: any = yield Telephony.PostHungUpCall(callId);

        return response.data;
      } catch (err) {
        console.error(err);

        throw err;
      }
    }),

    changeTag: flow(function* (id: string, params: { [key: string]: string }) {
      try {
        const response = yield Telephony.ChangeTag(self.updateUrl, id, params);

        self.calls.forEach((call: ICall) => {
          if (call.id === id) {
            call.tag = response.data;
          }
        });
      } catch (e) {
        console.error(e);
      }
    }),

    changeRating: flow(function* (
      id: string,
      params: { [key: string]: string }
    ) {
      try {
        const response = yield Telephony.ChangeRating(
          self.updateUrl,
          id,
          params
        );

        self.calls.forEach((call: ICall) => {
          if (call.id === id) {
            call.rating = response.data;
          }
        });
      } catch (e) {
        console.error(e);
      }
    }),

    changeCaller: flow(function* (id: string, params: any) {
      try {
        self.statuses.changeCallerRequest.fulfilled = false;
        self.statuses.changeCallerRequest.error = false;
        self.statuses.changeCallerRequest.pending = true;

        const response = yield Telephony.ChangeCaller(
          self.updateUrl,
          id,
          params
        );
        self.statuses.changeCallerRequest.fulfilled = true;

        self.calls.forEach((call: ICall) => {
          if (call.id === id) {
            call.caller = response.data;
          }
        });

        return response;
      } catch (e) {
        self.statuses.changeCallerRequest.error = true;
        console.error(e);
      } finally {
        self.statuses.changeCallerRequest.pending = false;
      }
    }),

    addNote: flow(function* (id: string, params: { [key: string]: string }) {
      try {
        const response = yield Telephony.AddNote(self.updateUrl, id, params);

        if (self.selectedCall) {
          self.selectedCall.note = response.data;
        }
      } catch (e) {
        console.error(e);
      }
    }),

    addComment: flow(function* (
      id: string,
      params: { [key: string]: string },
      inCallCard: boolean
    ) {
      try {
        const response = yield Telephony.AddComment(self.updateUrl, id, params);

        if (!inCallCard && self.selectedCall) {
          self.selectedCall.comments.push(response.data);
        }
      } catch (e) {
        console.error(e);
      }
    }),

    makeCallback: flow(function* (dst: string) {
      try {
        const response = yield Telephony.MakeCallback(dst);

        return response;
      } catch (err) {
        console.error(err);

        return Promise.reject(err);
      }
    }),
    makeRedial: flow(function* (uniqueid: string) {
      try {
        const response = yield Telephony.MakeRedial(uniqueid);

        return response;
      } catch (err) {
        console.error(err);

        return Promise.reject(err);
      }
    }),
    makeCallbackOrRedial: flow(function* (
      uniqueid: string,
      number: string,
      missed: boolean
    ) {
      try {
        if (!missed) {
          return yield Telephony.MakeCallback(number);
        }

        return yield Telephony.MakeRedial(uniqueid);
      } catch (err) {
        console.error('Error in sendCallback:', err);

        return Promise.reject(err);
      }
    }),
    updateCall: flow(function* (
      callid: string,
      params: { [key: string]: string }
    ) {
      try {
        self.statuses.updateCall.loadingStates();
        const response = yield Telephony.UpdateCall({
          callid,
          ...params,
        });
        self.statuses.updateCall.successStates();

        return response;
      } catch (err) {
        console.error(err);
        self.statuses.updateCall.errorStates();

        return Promise.reject(err);
      } finally {
        self.statuses.updateCall.finallyStates();
      }
    }),
  }));
